/**
 * Utility functions for syntax highlighting code blocks in markdown content
 */

/**
 * Adds syntax highlighting classes to code blocks in HTML content
 * @param container - The mounted element containing code blocks to highlight
 * @returns {void} Updates the code blocks in place
 */
export function highlightCodeBlocks(container: HTMLElement): void {
  // Find all code blocks
  const codeBlocks = container.querySelectorAll('pre > code');

  // Add appropriate classes for syntax highlighting
  codeBlocks.forEach((codeBlock) => {
    // Get the language from the class attribute if it exists
    const classAttr = codeBlock.getAttribute('class') || '';
    const languageMatch = classAttr.match(/language-(\w+)/);
    const language = languageMatch ? languageMatch[1] : '';

    // Add token classes to highlight specific parts
    if (language) {
      // Add a class to the pre element for styling
      const preElement = codeBlock.parentElement;
      if (preElement) {
        preElement.classList.add(`language-${language}`);
        preElement.classList.add('group'); // Add group class for hover effects
        preElement.classList.add('relative'); // Add relative position for the clipboard button

        // Make sure pre has proper styling
        preElement.style.backgroundColor = '#18181b';
        // Set a custom attribute to indicate this has custom syntax highlighting
        preElement.setAttribute('data-syntax-highlighted', 'true');

        // Store the raw code content as a data attribute for the clipboard functionality
        const codeContent = codeBlock.textContent || '';
        preElement.setAttribute('data-code-content', codeContent);

        preElement.style.padding = '16px';
        preElement.style.borderRadius = '6px';
        preElement.style.margin = '1em 0';
        preElement.style.overflow = 'auto';
        // Add a subtle lighter border
        preElement.style.border = '1px solid #1f1f23';
      }

      // Get the content - we'll work with the raw text to avoid HTML complications
      const content = codeBlock.textContent || '';

      // This is a simplified approach - we're using regex to add token classes
      let highlightedContent = '';

      // Apply syntax highlighting based on language
      // Keywords
      const keywords = [
        'const',
        'let',
        'var',
        'function',
        'return',
        'if',
        'else',
        'for',
        'while',
        'class',
        'interface',
        'type',
        'enum',
        'import',
        'export',
        'from',
        'as',
        'async',
        'await',
        'try',
        'catch',
        'throw',
        'new',
        'this',
        'extends',
        'implements',
        'public',
        'private',
        'protected',
        'static',
        'get',
        'set',
        'namespace',
        'package',
        'module',
        'true',
        'false',
        'null',
        'undefined',
        'switch',
        'case',
        'break',
        'continue',
        'default',
        'void',
        'typeof',
        'instanceof',
        'super',
        'yield',
        'in',
        'of',
        'do',
        'finally',
        'with',
        'delete',
      ];

      // Simple tokenizer that preserves HTML entity encoding
      const lines = content.split('\n');
      let inMultiLineComment = false;

      const tokenizedLines = lines.map((line) => {
        let tokenizedLine = '';
        let i = 0;

        while (i < line.length) {
          // Handle multi-line comment continuation
          if (inMultiLineComment) {
            const endIndex = line.indexOf('*/', i);
            if (endIndex !== -1) {
              // Found the end of multi-line comment
              tokenizedLine += `<span class="token comment">${encodeHTML(line.substring(i, endIndex + 2))}</span>`;
              i = endIndex + 2;
              inMultiLineComment = false;
            } else {
              // Multi-line comment continues to next line
              tokenizedLine += `<span class="token comment">${encodeHTML(line.substring(i))}</span>`;
              i = line.length;
            }
            continue;
          }

          // Check for start of multi-line comment
          if (line.substring(i, i + 2) === '/*') {
            const endIndex = line.indexOf('*/', i + 2);
            if (endIndex !== -1) {
              // Multi-line comment starts and ends on same line
              tokenizedLine += `<span class="token comment">${encodeHTML(line.substring(i, endIndex + 2))}</span>`;
              i = endIndex + 2;
            } else {
              // Multi-line comment continues to next line
              tokenizedLine += `<span class="token comment">${encodeHTML(line.substring(i))}</span>`;
              inMultiLineComment = true;
              i = line.length;
            }
            continue;
          }

          // Check for single-line comment
          if (i + 1 < line.length && line.substring(i, i + 2) === '//') {
            const commentEnd = line.length;
            tokenizedLine += `<span class="token comment">${encodeHTML(line.substring(i, commentEnd))}</span>`;
            i = commentEnd;
            continue;
          }

          // Check for string literal
          if (line[i] === '"' || line[i] === "'" || line[i] === '`') {
            const quote = line[i];
            let end = i + 1;
            while (end < line.length && line[end] !== quote) {
              if (line[end] === '\\') {
                end += 2;
              } else {
                end++;
              }
            }
            if (end < line.length) {
              end++;
            } // Include closing quote
            tokenizedLine += `<span class="token string">${encodeHTML(line.substring(i, end))}</span>`;
            i = end;
            continue;
          }

          // Check for keyword
          let foundKeyword = false;
          for (const keyword of keywords) {
            if (
              line.substring(i, i + keyword.length) === keyword &&
              (i === 0 || !isAlphaNumeric(line.charAt(i - 1))) &&
              (i + keyword.length === line.length ||
                !isAlphaNumeric(line.charAt(i + keyword.length)))
            ) {
              tokenizedLine += `<span class="token keyword">${keyword}</span>`;
              i += keyword.length;
              foundKeyword = true;
              break;
            }
          }
          if (foundKeyword) {
            continue;
          }

          // Check for function call
          const functionMatch = /^(\w+)(\s*\()/.exec(line.substring(i));
          if (functionMatch && functionMatch[1] && functionMatch[2]) {
            tokenizedLine += `<span class="token function">${functionMatch[1]}</span>${functionMatch[2]}`;
            i += functionMatch[0].length;
            continue;
          }

          // Check for number
          const numberMatch = /^(\d+(?:\.\d+)?)/.exec(line.substring(i));
          if (numberMatch && numberMatch[1] && (i === 0 || !isAlphaNumeric(line.charAt(i - 1)))) {
            tokenizedLine += `<span class="token number">${numberMatch[1]}</span>`;
            i += numberMatch[1].length;
            continue;
          }

          // Punctuation
          if (/^[{}[\]();,.:?]/.test(line.substring(i))) {
            tokenizedLine += `<span class="token punctuation">${line[i]}</span>`;
            i++;
            continue;
          }

          // Default case: just add the character
          tokenizedLine += encodeHTML(line.charAt(i));
          i++;
        }

        return tokenizedLine;
      });

      // Join lines and set content
      highlightedContent = tokenizedLines.join('\n');
      codeBlock.innerHTML = highlightedContent;
    }
  });
}

/**
 * Checks if a character is alphanumeric
 * @param char - The character to check
 * @returns True if the character is alphanumeric or underscore
 */
function isAlphaNumeric(char: string): boolean {
  return /\w/.test(char);
}

/**
 * Encodes HTML special characters to prevent XSS
 * @param str - The string to encode
 * @returns The encoded string
 */
function encodeHTML(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
